import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, MapPin, Video, Play, Pause, Plus, ClipboardCheck,
  Ban, CheckCircle2, DollarSign, CalendarClock, Timer, ReceiptText, MessageSquare, ShieldCheck
} from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import StatusPill from "@/components/marketplace/StatusPill";
import StripeCheckoutPanel from "@/components/marketplace/StripeCheckoutPanel";
import TipButton from "@/components/marketplace/TipButton";
import MessageThread from "@/components/marketplace/MessageThread";
import ReviewForm from "@/components/marketplace/ReviewForm";
import OpenDisputeDialog from "@/components/marketplace/OpenDisputeDialog";
import { ShieldAlert, MapPinned } from "lucide-react";
import { JobTrackerPanel } from "@/components/jobs/JobTrackerPanel";

const TABS = [
  { key: "overview", label: "Overview", icon: ClipboardCheck },
  { key: "tracker", label: "Job tracker", icon: MapPinned },
  { key: "timer", label: "Timer & expenses", icon: Timer },
  { key: "activity", label: "Activity log", icon: ReceiptText },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

export default function BookingWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [myReview, setMyReview] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  useDocumentTitle(data?.booking?.service_title ? `Booking · ${data.booking.service_title}` : "Booking");

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setData(data);
    } catch (err) {
      toast.error("Could not load booking");
      navigate("/dashboard/bookings");
    }
  };
  useEffect(() => { load(); }, [id]);

  // Load existing review (for the current user) once we have the booking id.
  useEffect(() => {
    if (!data?.booking?.id) return;
    (async () => {
      try {
        const { data: mine } = await api.get("/reviews/mine");
        const found = (mine || []).find((r) => (r.booking_id === data.booking.id || r.target_id === data.booking.id) && r.reviewer_id === user?.id);
        setMyReview(found || null);
      } catch (_) { /* ignore */ }
    })();
  }, [data?.booking?.id, user?.id]);

  if (!data) return <DashboardShell><div className="p-6 text-[#252A34]/60">Loading booking…</div></DashboardShell>;

  const b = data.booking;
  const isProvider = b.provider_user_id === user?.id;
  const isCustomer = b.customer_id === user?.id;

  const transition = async (to) => {
    try {
      await api.post(`/bookings/${b.id}/transition?to=${to}`);
      toast.success(`Status → ${to.replace(/_/g, " ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Cannot transition");
    }
  };

  const cancel = async () => {
    const reason = window.prompt("Reason for canceling? (optional)") || undefined;
    try {
      const { data: r } = await api.post(`/bookings/${b.id}/cancel`, { reason });
      toast.success(`Canceled. Est refund: $${r.refund_amount?.toFixed(2)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Cannot cancel");
    }
  };

  const complete = async () => {
    try {
      await api.post(`/bookings/${b.id}/complete`, { completion_notes: "Work completed.", completion_photos: [] });
      toast.success("Marked complete — awaiting customer approval");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Cannot complete");
    }
  };

  const approveCompletion = async () => {
    try {
      await api.post(`/bookings/${b.id}/approve-completion`);
      toast.success("Great — job approved!");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Cannot approve");
    }
  };
  void approveCompletion;

  return (
    <DashboardShell>
      <div className="max-w-6xl">
        <button onClick={() => navigate(-1)} className="text-sm text-[#165a71] font-medium inline-flex items-center gap-2 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{b.kind === "consultation" ? "Consultation" : "Local booking"}</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]" data-testid="booking-title">{b.service_title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[#252A34]/70">
              <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> {new Date(b.scheduled_start).toLocaleString()}</span>
              {b.address_snapshot && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {b.address_snapshot.city}, {b.address_snapshot.state}</span>}
              {b.meeting_link && <a href="#" className="inline-flex items-center gap-1.5 text-[#165a71]"><Video className="h-4 w-4" /> Meeting link</a>}
            </div>
          </div>
          <StatusPill status={b.status} size="lg" />
        </div>

        {/* Payment */}
        {b.status === "payment_pending" && isCustomer && (
          <div className="mb-6" data-testid="pay-panel">
            <StripeCheckoutPanel
              purpose={b.kind === "consultation" ? "consultation" : "booking"}
              targetId={b.id}
              amount={b.total_estimate}
              ctaLabel="Pay with card"
            />
          </div>
        )}
        {b.status === "completed" && isCustomer && (
          <div className="mb-6 flex justify-end gap-2 flex-wrap">
            {!b.is_disputed && <button onClick={() => setDisputeOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white text-rose-700 px-4 py-2 text-sm font-medium hover:bg-rose-50" data-testid="booking-open-dispute-btn"><ShieldAlert className="h-4 w-4" /> Open dispute</button>}
            <TipButton targetId={b.id} providerName={data.provider?.business_name || data.provider?.display_name} testId="booking-tip-btn" />
          </div>
        )}
        {b.status === "completed" && isProvider && !b.is_disputed && (
          <div className="mb-6 flex justify-end">
            <button onClick={() => setDisputeOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white text-rose-700 px-4 py-2 text-sm font-medium hover:bg-rose-50" data-testid="booking-open-dispute-btn"><ShieldAlert className="h-4 w-4" /> Open dispute</button>
          </div>
        )}
        {b.is_disputed && (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="booking-dispute-banner">
            This booking is currently <b>in dispute</b>. Provider payout is held in the reserved balance until it's resolved.
            {b.dispute_id && <> <Link className="underline font-medium ml-1" to={`/dashboard/disputes/${b.dispute_id}`}>Open case →</Link></>}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6 flex gap-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 pb-3 text-sm font-medium border-b-2 ${tab === t.key ? "border-[#165a71] text-[#071b22]" : "border-transparent text-[#252A34]/60 hover:text-[#252A34]"}`}
              data-testid={`booking-tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              <Card title="Service">
                <div className="text-sm text-[#252A34]">{data.service?.short_description || b.service_title || "—"}</div>
                {b.instructions && (
                  <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
                    <div className="text-xs uppercase text-[#252A34]/60 font-medium mb-1">Customer instructions</div>
                    {b.instructions}
                  </div>
                )}
              </Card>
              {b.address_snapshot && (
                <Card title="Address">
                  <div className="text-sm text-[#252A34]">{b.address_snapshot.line1}{b.address_snapshot.line2 ? `, ${b.address_snapshot.line2}` : ""}</div>
                  <div className="text-sm text-[#252A34]">{b.address_snapshot.city}, {b.address_snapshot.state} {b.address_snapshot.zip_code}</div>
                  {b.address_snapshot.access_instructions && (
                    <div className="text-xs text-[#252A34]/60 mt-2">Access: {b.address_snapshot.access_instructions}</div>
                  )}
                </Card>
              )}

              <Card title="Actions">
                <div className="flex flex-wrap gap-2">
                  {isProvider && b.status === "awaiting_provider_response" && (
                    <ActionBtn onClick={() => transition("confirmed")} data-testid="btn-confirm">Confirm booking</ActionBtn>
                  )}
                  {isProvider && b.status === "confirmed" && (
                    <ActionBtn onClick={() => transition("scheduled")}>Mark scheduled</ActionBtn>
                  )}
                  {isProvider && b.kind !== "consultation" && ["scheduled", "confirmed", "provider_traveling", "provider_arrived", "in_progress", "paused", "awaiting_expense_approval", "awaiting_customer_completion_approval"].includes(b.status) && (
                    <ActionBtn onClick={() => navigate(`/dashboard/jobs/${b.id}`)} data-testid="btn-open-active-job"><MapPinned className="h-4 w-4 mr-1" /> Open Active Job Screen</ActionBtn>
                  )}
                  {isProvider && b.kind === "consultation" && ["scheduled", "provider_traveling"].includes(b.status) && (
                    <ActionBtn onClick={() => transition("provider_arrived")}>I've joined</ActionBtn>
                  )}
                  {isProvider && b.kind === "consultation" && b.status === "provider_arrived" && (
                    <ActionBtn onClick={() => transition("in_progress")}>Start session</ActionBtn>
                  )}
                  {isProvider && b.kind === "consultation" && ["in_progress", "awaiting_expense_approval"].includes(b.status) && (
                    <ActionBtn onClick={complete} data-testid="btn-complete">Mark complete</ActionBtn>
                  )}
                  {isCustomer && b.status === "awaiting_customer_completion_approval" && (
                    <ActionBtn variant="emerald" onClick={() => setTab("tracker")} data-testid="btn-approve-completion">Review & approve completed work</ActionBtn>
                  )}
                  {!["completed", "canceled", "refunded"].includes(b.status) && (
                    <ActionBtn variant="ghost" onClick={cancel} data-testid="btn-cancel"><Ban className="h-4 w-4 mr-1" /> Cancel</ActionBtn>
                  )}
                </div>
              </Card>
            </div>
            <div className="space-y-4">
              <PricingCard b={b} />
              <ParticipantCard title={isProvider ? "Customer" : "Provider"} name={isProvider ? "Customer" : (data.provider?.business_name || data.provider?.display_name)} img={data.provider?.profile_image} />
            </div>
          </div>
        )}

        {tab === "tracker" && (
          <JobTrackerPanel booking={b} isCustomer={isCustomer} isProvider={isProvider} onChange={load} />
        )}

        {tab === "timer" && (
          <TimerAndExpenses b={b} isProvider={isProvider} isCustomer={isCustomer} onChange={load} />
        )}

        {tab === "activity" && (
          <ActivityLog b={b} />
        )}

        {tab === "messages" && (
          <div className="max-w-3xl">
            <MessageThread conversationId={b.conversation_id} otherPartyName={isProvider ? "Customer" : (data.provider?.business_name || data.provider?.display_name)} />
          </div>
        )}

        {b.status === "completed" && !myReview && (isCustomer || isProvider) && (
          <div className="mt-8" data-testid="booking-review-slot">
            <ReviewForm
              transaction={{ ...b, target_collection: "bookings" }}
              direction={isCustomer ? "customer_to_provider" : "provider_to_customer"}
              onSubmitted={() => { setMyReview({ pending: true }); load(); }}
            />
          </div>
        )}
      </div>
      {disputeOpen && (
        <OpenDisputeDialog
          open={disputeOpen}
          onClose={() => setDisputeOpen(false)}
          transaction={{ ...b, target_collection: "bookings" }}
          onOpened={(d) => { toast.success("Case opened"); load(); if (d?.id) navigate(`/dashboard/disputes/${d.id}`); }}
        />
      )}
    </DashboardShell>
  );
}

// ----- Sub-components -----

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-display font-semibold text-[#071b22] mb-3">{title}</div>
      {children}
    </div>
  );
}

function ActionBtn({ children, onClick, variant, ...props }) {
  const styles = {
    default: "bg-[#165a71] text-white hover:bg-[#11485a]",
    emerald: "bg-emerald-600 text-white hover:bg-emerald-700",
    ghost: "border border-slate-200 text-[#252A34] hover:bg-slate-50",
  };
  return (
    <button onClick={onClick} className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-medium transition ${styles[variant || "default"]}`} {...props}>
      {children}
    </button>
  );
}

function PricingCard({ b }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="font-display font-semibold text-[#071b22] mb-3">Pricing</div>
      <div className="space-y-1.5 text-sm">
        <Row label="Subtotal" value={`$${(b.subtotal || 0).toFixed(2)}`} />
        {b.addons_total > 0 && <Row label="Add-ons" value={`+$${b.addons_total.toFixed(2)}`} />}
        {b.travel_fee > 0 && <Row label="Travel" value={`+$${b.travel_fee.toFixed(2)}`} />}
        {b.weekend_fee > 0 && <Row label="Weekend" value={`+$${b.weekend_fee.toFixed(2)}`} />}
        {b.emergency_fee > 0 && <Row label="Emergency" value={`+$${b.emergency_fee.toFixed(2)}`} />}
        {b.expenses_total > 0 && <Row label="Expenses" value={`+$${b.expenses_total.toFixed(2)}`} />}
        {b.tip_amount > 0 && <Row label="Tip" value={`+$${b.tip_amount.toFixed(2)}`} />}
      </div>
      <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
        <div className="text-sm text-[#252A34]/70">Total</div>
        <div className="font-display text-xl font-semibold text-[#071b22]">${(b.total_estimate || 0).toFixed(2)}</div>
        {b.deal_discount > 0 && <div className="text-xs text-[#8a3b1e] mt-0.5" data-testid="booking-deal-line">Includes deal "{b.deal_title}" (−${Number(b.deal_discount).toFixed(2)})</div>}
      </div>
      <div className="mt-2 text-[11px] text-[#252A34]/60">Payment: {b.payment_status}{b.payment_simulated ? " (DEV simulated)" : ""}</div>
    </div>
  );
}

function ParticipantCard({ title, name, img }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-3">
      <div className="h-12 w-12 rounded-2xl overflow-hidden bg-[#165a71]/10 flex items-center justify-center text-[#165a71] font-bold">
        {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : (name || "?").slice(0, 1)}
      </div>
      <div>
        <div className="text-xs uppercase text-[#252A34]/60">{title}</div>
        <div className="font-display font-semibold text-[#071b22]">{name}</div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#252A34]/70">{label}</span>
    <span className="font-medium text-[#252A34]">{value}</span>
  </div>
);

function TimerAndExpenses({ b, isProvider, isCustomer, onChange }) {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [addTime, setAddTime] = useState(30);

  const send = async (action, extra = {}) => {
    try {
      await api.post(`/bookings/${b.id}/timer`, { action, ...extra });
      toast.success(`Timer: ${action.replace(/_/g, " ")}`);
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const submitExpense = async () => {
    if (!desc || !amount) return toast.error("Description and amount required");
    try {
      await api.post(`/bookings/${b.id}/expenses/request`, { description: desc, amount: Number(amount) });
      toast.success("Expense submitted for approval");
      setDesc(""); setAmount("");
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const decideExpense = async (expense_id, approve) => {
    try {
      await api.post(`/bookings/${b.id}/expenses/approval`, { expense_id, approve });
      toast.success(`Expense ${approve ? "approved" : "declined"}`);
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const decideTime = async (event_id, accept) => {
    try {
      await api.post(`/bookings/${b.id}/timer/approval`, { event_id, accept });
      toast.success(`Add-time ${accept ? "approved" : "declined"}`);
      onChange();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  const events = b.timer_events || [];
  const pendingAddTime = events.filter((e) => e.action === "request_add_time" && e.status === "awaiting_customer");

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="timer-card">
        <div className="font-display font-semibold text-[#071b22]">Time tracked</div>
        <div className="mt-2 text-4xl font-display font-bold text-[#165a71]">{Math.floor((b.logged_minutes || 0) / 60)}h {(b.logged_minutes || 0) % 60}m</div>
        {isProvider && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button className="btn-primary" onClick={() => send("start")}><Play className="h-4 w-4 mr-1" /> Start</button>
            <button className="btn-ghost" onClick={() => send("pause")}><Pause className="h-4 w-4 mr-1" /> Pause</button>
            <button className="btn-ghost" onClick={() => send("resume")}><Play className="h-4 w-4 mr-1" /> Resume</button>
            <div className="flex items-center gap-2">
              <input type="number" value={addTime} onChange={(e) => setAddTime(e.target.value)} className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm" />
              <button className="btn-ghost" onClick={() => send("request_add_time", { add_minutes: Number(addTime) })}><Plus className="h-4 w-4 mr-1" /> Request add-time</button>
            </div>
          </div>
        )}
        {isCustomer && pendingAddTime.length > 0 && (
          <div className="mt-4 space-y-2">
            {pendingAddTime.map((e) => (
              <div key={e.id} className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center justify-between text-sm">
                <div>Provider requested +{e.add_minutes} min</div>
                <div className="flex gap-2">
                  <button className="btn-primary text-xs" onClick={() => decideTime(e.id, true)}>Approve</button>
                  <button className="btn-ghost text-xs" onClick={() => decideTime(e.id, false)}>Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 text-xs text-[#252A34]/60">
          {events.length} timer event{events.length === 1 ? "" : "s"} recorded.
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="expense-card">
        <div className="font-display font-semibold text-[#071b22]">Expenses</div>
        <div className="mt-2 text-sm text-[#252A34]/70">Requested & approved out-of-pocket items get added to your invoice.</div>
        {isProvider && (
          <div className="mt-4 space-y-2">
            <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#252A34]/70"><DollarSign className="h-4 w-4 inline" /></span>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-32 rounded-lg border border-slate-200 px-2 py-2 text-sm" />
              <button className="btn-primary" onClick={submitExpense}>Request approval</button>
            </div>
          </div>
        )}
        <div className="mt-3 space-y-2">
          {(b.expenses || []).map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 p-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{e.description}</div>
                <div className="text-xs text-[#252A34]/60">${e.amount?.toFixed(2)} · {e.status}</div>
              </div>
              {isCustomer && e.status === "pending" && (
                <div className="flex gap-2">
                  <button className="btn-primary text-xs" onClick={() => decideExpense(e.id, true)}>Approve</button>
                  <button className="btn-ghost text-xs" onClick={() => decideExpense(e.id, false)}>Decline</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <style>{`
        .btn-primary { display: inline-flex; align-items: center; padding: 0.4rem 0.9rem; border-radius: 999px; font-size: 0.8rem; font-weight: 500; background: #165a71; color: white; }
        .btn-primary:hover { background: #11485a; }
        .btn-ghost { display: inline-flex; align-items: center; padding: 0.4rem 0.9rem; border-radius: 999px; font-size: 0.8rem; border: 1px solid #e2e8f0; color: #252A34; }
        .btn-ghost:hover { background: #f8fafc; }
      `}</style>
    </div>
  );
}

function ActivityLog({ b }) {
  const items = [...(b.status_history || []), ...(b.timer_events || []).map((e) => ({ ...e, kind: "timer" })), ...(b.expenses || []).map((e) => ({ ...e, kind: "expense" }))];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="font-display font-semibold text-[#071b22] mb-3">Activity log</div>
      <div className="text-xs text-[#252A34]/60 mb-3">Every status transition, timer event, and expense is logged for invoices & disputes.</div>
      <div className="space-y-3">
        {(b.status_history || []).map((h, i) => (
          <div key={i} className="flex items-start gap-3 text-sm" data-testid={`activity-row-${i}`}>
            <div className="mt-1 h-2 w-2 rounded-full bg-[#165a71]" />
            <div className="flex-1">
              <div className="font-medium">Status: <span className="capitalize">{h.from?.replace(/_/g, " ")} → {h.to?.replace(/_/g, " ")}</span></div>
              <div className="text-xs text-[#252A34]/60">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
              {h.meta?.dev_simulated_payment && <div className="text-[11px] uppercase tracking-wider text-amber-700 font-medium">DEV simulated payment</div>}
            </div>
          </div>
        ))}
        {(b.timer_events || []).map((e, i) => (
          <div key={"t" + i} className="flex items-start gap-3 text-sm">
            <div className="mt-1 h-2 w-2 rounded-full bg-teal-500" />
            <div className="flex-1">
              <div className="font-medium">Timer: <span className="capitalize">{e.action?.replace(/_/g, " ")}</span></div>
              <div className="text-xs text-[#252A34]/60">{e.at ? new Date(e.at).toLocaleString() : ""}{e.add_minutes ? ` · +${e.add_minutes} min` : ""}{e.status ? ` · ${e.status}` : ""}</div>
            </div>
          </div>
        ))}
        {(b.expenses || []).map((e, i) => (
          <div key={"e" + i} className="flex items-start gap-3 text-sm">
            <div className="mt-1 h-2 w-2 rounded-full bg-amber-500" />
            <div className="flex-1">
              <div className="font-medium">Expense: {e.description} · ${e.amount?.toFixed(2)}</div>
              <div className="text-xs text-[#252A34]/60">{e.requested_at ? new Date(e.requested_at).toLocaleString() : ""} · {e.status}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
